import { FieldErrors } from 'react-hook-form'

import {
  BuildEvaluationPayloadOptions,
  CompetitorEvaluation,
  EvaluationFormValues
} from './EvaluationModal.types'

const isFilledValue = (value: unknown) =>
  value !== null && value !== undefined && (typeof value !== 'string' || value.trim() !== '')

export const formatPercentValue = (value: number | null, digits = 1) => {
  if (value === null || value === undefined) return '--'

  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })
}

export const formatSalesValue = (value: number | null) => {
  if (value === null || value === undefined) return '--'

  if (value >= 1000) {
    return `${(value / 1000).toLocaleString('pt-BR', {
      maximumFractionDigits: 1
    })} mil`
  }

  return value.toLocaleString('pt-BR')
}

export const formatPresenceValue = (value: number | null) => {
  if (value === null || value === undefined) return '--'

  if (value <= 1) return 'Fraca'

  if (value === 2) return 'Médio'

  return 'Forte'
}

export const getSliderValue = (value: number | number[] | null | undefined): number | null => {
  const normalized = Array.isArray(value) ? value[0] : value

  return normalized ?? null
}

export const compactObject = <T extends Record<string, unknown>>(obj: T) =>
  Object.fromEntries(Object.entries(obj).filter(([, value]) => isFilledValue(value)))

const normalizeOptionalString = (value: unknown) => {
  if (value == null) return ''

  if (typeof value === 'string') return value.trim()

  return String(value).trim()
}

const hasFilledOptionalString = (value: unknown) => normalizeOptionalString(value) !== ''

export const EMPTY_PRIORITIES_MESSAGE = 'Não existem prioridades disponíveis.'

const isPlaceholderPriorityLabel = (trimmed: string) => /^[-–—\s]+$/.test(trimmed)

export const hasValidPriorityLabel = (label?: string | null) => {
  if (typeof label !== 'string') return false

  const trimmed = label.trim()

  return trimmed.length > 0 && !isPlaceholderPriorityLabel(trimmed)
}

export type PriorityOptionLabel = {
  id?: string | number | null
  name?: string | null
}

export const hasValidPriorityOptionId = (value: unknown) => {
  if (value === null || value === undefined) return false

  if (typeof value === 'number') return Number.isFinite(value)

  if (typeof value !== 'string') return false

  const trimmed = value.trim()

  return (
    trimmed.length > 0 && trimmed.toLowerCase() !== 'null' && !isPlaceholderPriorityLabel(trimmed)
  )
}

export const hasAvailablePriorityOptions = (options?: readonly PriorityOptionLabel[] | null) =>
  (options ?? []).some(
    (item) => hasValidPriorityOptionId(item?.id) && hasValidPriorityLabel(item?.name)
  )

export type EvaluationPrioritySchemaContext = {
  showPriorityAgreement?: boolean
  showPriorityOptions?: boolean
}

export const readEvaluationPrioritySchemaContext = (
  context: unknown
): EvaluationPrioritySchemaContext => {
  if (!context || typeof context !== 'object') return {}

  return context as EvaluationPrioritySchemaContext
}

export const getPriorityEvaluationVisibility = ({
  currentPriorityLabel,
  priorityOptions,
  agreement
}: {
  currentPriorityLabel?: string | null
  priorityOptions?: readonly PriorityOptionLabel[] | null
  agreement?: EvaluationFormValues['priority_agreement']
}) => {
  const showPriorityAgreement = hasValidPriorityLabel(currentPriorityLabel)

  const hasPriorityOptions = hasAvailablePriorityOptions(priorityOptions)

  const showPriorityOptions =
    showPriorityAgreement && agreement === 'disagree' && hasPriorityOptions

  const showEmptyPrioritiesMessage =
    !hasPriorityOptions && (!showPriorityAgreement || agreement === 'disagree')

  return {
    showPriorityAgreement,
    showPriorityOptions,
    showEmptyPrioritiesMessage
  }
}

export const sanitizePriorityEvaluationValues = (
  values: EvaluationFormValues,
  visibility: EvaluationPrioritySchemaContext
): EvaluationFormValues => ({
  ...values,
  priority_agreement: visibility.showPriorityAgreement ? values.priority_agreement : null,
  priority_new: visibility.showPriorityOptions ? values.priority_new : null,
  priority_comment:
    visibility.showPriorityAgreement && values.priority_agreement === 'disagree'
      ? values.priority_comment
      : ''
})

type IsCompetitorStartedOptions = {
  countComment?: boolean
}

export const isCompetitorStarted = (
  competitor: CompetitorEvaluation,
  options?: IsCompetitorStartedOptions
) => {
  const countComment = options?.countComment ?? true

  return (
    Boolean(competitor.id) ||
    competitor.price != null ||
    competitor.coupon != null ||
    competitor.entry != null ||
    competitor.commission_store != null ||
    competitor.commission_sales != null ||
    competitor.fair != null ||
    competitor.presence != null ||
    competitor.less_bureaucracy != null ||
    (countComment && competitor.comment.trim() !== '')
  )
}

export const isPrioritySectionStarted = (values: EvaluationFormValues) =>
  values.priority_score != null ||
  values.priority_agreement != null ||
  hasFilledOptionalString(values.priority_new) ||
  values.priority_comment.trim() !== ''

export const isTabSectionStarted = (
  values: EvaluationFormValues,
  options?: BuildEvaluationPayloadOptions
) => {
  if (values.tab_fidelity != null || values.tab_fee != null || values.tab_approval != null) {
    return true
  }

  if ((values.tab_images?.length ?? 0) > 0) return true

  const competitors = values.competitors ?? []

  const countCompetitorComment = !options?.isGoodsAndServices

  if (competitors[1]?.enabled) return true

  return competitors.some((competitor) =>
    isCompetitorStarted(competitor, { countComment: countCompetitorComment })
  )
}

export const isPrioritySectionComplete = (values: EvaluationFormValues) =>
  values.priority_score != null && values.priority_score >= 1

export const isTabSectionComplete = (values: EvaluationFormValues) =>
  values.tab_fidelity != null &&
  values.tab_fidelity >= 1 &&
  values.tab_fee != null &&
  values.tab_fee >= 1 &&
  values.tab_approval != null &&
  values.tab_approval >= 1

export const hasPrioritySectionErrors = (errors: FieldErrors<EvaluationFormValues>) =>
  Boolean(
    errors.priority_score ??
      errors.priority_agreement ??
      errors.priority_new ??
      errors.priority_comment
  )

export const hasTabSectionErrors = (errors: FieldErrors<EvaluationFormValues>) =>
  Boolean(
    errors.tab_fidelity ??
      errors.tab_fee ??
      errors.tab_approval ??
      errors.tab_images ??
      errors.competitors
  )

export const hasPrioritySectionAttention = (
  values: EvaluationFormValues,
  errors: FieldErrors<EvaluationFormValues>
) => {
  if (!isPrioritySectionStarted(values)) return false

  return !isPrioritySectionComplete(values) || hasPrioritySectionErrors(errors)
}

export const hasTabSectionAttention = (
  values: EvaluationFormValues,
  errors: FieldErrors<EvaluationFormValues>,
  options?: BuildEvaluationPayloadOptions
) => {
  if (!isTabSectionStarted(values, options)) return false

  return !isTabSectionComplete(values) || hasTabSectionErrors(errors)
}

export const canSubmitEvaluationForm = (
  values: EvaluationFormValues,
  errors: FieldErrors<EvaluationFormValues>,
  options?: BuildEvaluationPayloadOptions
) => {
  const isGoodsAndServices = options?.isGoodsAndServices ?? false

  if (isGoodsAndServices) {
    if (!isTabSectionStarted(values, options)) return false

    if (hasTabSectionAttention(values, errors, options)) return false

    return true
  }

  const priorityStarted = isPrioritySectionStarted(values)

  const tabStarted = isTabSectionStarted(values, options)

  if (!priorityStarted && !tabStarted) return false

  if (priorityStarted && hasPrioritySectionAttention(values, errors)) return false

  if (tabStarted && hasTabSectionAttention(values, errors, options)) return false

  return true
}

export const buildEvaluationPayload = (
  values: EvaluationFormValues,
  options?: BuildEvaluationPayloadOptions
) => {
  const isGoodsAndServices = options?.isGoodsAndServices ?? false

  const priority = isPrioritySectionComplete(values)
    ? compactObject({
        score: values.priority_score,
        agreement: values.priority_agreement,
        new_priority: hasFilledOptionalString(values.priority_new)
          ? normalizeOptionalString(values.priority_new)
          : null,
        comment: hasFilledOptionalString(values.priority_comment) ? values.priority_comment : null
      })
    : {}

  const tab = isTabSectionComplete(values)
    ? compactObject({
        fidelity: values.tab_fidelity,
        fee: values.tab_fee,
        approval: values.tab_approval,
        ...(isGoodsAndServices && hasFilledOptionalString(values.tab_comment)
          ? { comment: values.tab_comment }
          : {}),
        ...(values.tab_images?.length
          ? {
              attachments: values.tab_images.map((item) => ({
                storage_file_name: item.storage_file_name,
                file_name: item.file_name,
                file_url: item.url,
                file_size: item.size,
                extension: item.extension
              }))
            }
          : {})
      })
    : {}

  const competitors =
    values.competitors
      ?.filter(
        (competitor) =>
          competitor.enabled &&
          isCompetitorStarted(competitor, {
            countComment: !isGoodsAndServices
          })
      )
      .map((competitor) => {
        const payload = compactObject({
          competitor_id: competitor.id,
          price: competitor.price,
          coupon: competitor.coupon,
          entry: competitor.entry,
          commission_store: competitor.commission_store,
          commission_sales: competitor.commission_sales,
          fair: competitor.fair,
          presence: competitor.presence,
          less_bureaucracy: competitor.less_bureaucracy,
          ...(!isGoodsAndServices ? { comment: competitor.comment } : {})
        })

        return payload
      }) ?? []

  return {
    priority,
    tab,
    competitors
  }
}
